/**
 * Column definitions and row builder for the external single-order drug (J-code) table.
 *
 * @module singleOrderForExternal/components/drugTableForExternal/utils/generateTableDataForExternal
 */
import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import {
  AuthOnFile,
  JCodeDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { isExceptionFlowForRadiologyOrg } from "../../../../../enums/organizationTypes";
import { DrugTableForExternalKeys } from "../../../../../enums/tableColumnKeys";
import { getAuthStatusOfOneJCode } from "../../../../../utils/getAuthStatusOfMedicalPaOrder";

/**
 * Builds column definitions for the external single-order drug (J-code) table.
 *
 * Implemented as a factory so the J-codes column label can vary by
 * facility category — "CPT Codes" for radiology exception-flow facilities and
 * "J Codes" everywhere else. Evaluating at call time avoids freezing the label
 * at module import (before the org context is resolved).
 *
 * @param category - Healthcare facility `category`. May be `null` or
 *   `undefined` when the facility doc has not yet been resolved.
 * @returns Ordered column definitions consumed by the custom table component.
 */
export const getDrugTableForExternalColumnsKeys = (
  category?: string | null,
): TableHeader[] => [
  {
    label: "Date From",
    key: DrugTableForExternalKeys.StartFrom,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Date To",
    key: DrugTableForExternalKeys.EndAt,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: isExceptionFlowForRadiologyOrg(category) ? "CPT Codes" : "J Codes",
    key: DrugTableForExternalKeys.JCodes,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Description",
    key: DrugTableForExternalKeys.Description,
    order: 4,
    width: 25,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Total Visits",
    key: DrugTableForExternalKeys.Visits,
    order: 5,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Remaining",
    key: DrugTableForExternalKeys.RemainingVisits,
    order: 6,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Completed",
    key: DrugTableForExternalKeys.VisitsAllowed,
    order: 7,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Auth Required",
    key: DrugTableForExternalKeys.PaRequired,
    order: 8,
    width: 15,
    type: TableCellType.BADGE,
  },
];

/**
 * Flattens all J-codes from auth-on-file entries into table rows with formatted dates and badges.
 *
 * @param data - Auth-on-file object containing entries and their J-codes
 * @param authStatusOptions - Payer-specific labels/colors for PA-required column
 * @returns Array of row objects keyed by {@link DrugTableForExternalKeys}
 */
export const generateDrugTableForExternalData = (
  data: AuthOnFile,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  const authEntries = data?.entries ?? data?.auth_entries ?? [];
  const getAllJCodesData = () => {
    let JcodesData: JCodeDetails[] = [];
    authEntries.forEach((item) => {
      JcodesData.push(...(item?.j_codes ?? []));
    });
    return JcodesData;
  };
  const JcodesData = getAllJCodesData();
  return JcodesData.map((item, index) => {
    const authStatus = getAuthStatusOfOneJCode(item, authStatusOptions);

    const description = () => {
      if (item?.medicine_name && item?.medicine_name !== "") {
        return item?.medicine_name;
      }
      return item?.description ?? "N/A";
    };

    return {
      [DrugTableForExternalKeys.StartFrom]: item?.start_date
        ? moment(item.start_date).format("MM/DD/YYYY")
        : "",
      [DrugTableForExternalKeys.EndAt]: item?.end_date
        ? moment(item.end_date).format("MM/DD/YYYY")
        : "",
      [DrugTableForExternalKeys.Description]: description(),
      [DrugTableForExternalKeys.PaRequired]: {
        text: authStatus?.text,
        color: authStatus?.textColor,
        bgColor: authStatus?.bgColor,
      },
      [DrugTableForExternalKeys.Visits]: item?.num_visits ?? "0",
      [DrugTableForExternalKeys.RemainingVisits]: item?.visits_remaining ?? "0",
      [DrugTableForExternalKeys.VisitsAllowed]: item?.visits_happened ?? "0",
      [DrugTableForExternalKeys.JCodes]: item?.j_code ?? "N/A",
      [DrugTableForExternalKeys.JCodeID]: item?.id ?? "N/A",
    };
  });
};
